/**
 * WraithSkillEffect — Specter wraith-path active: stealth-preserving reposition.
 *
 * A short repositioning dash (~1 tile) that doesn't break Stealth. Consumes
 * 1 dash charge. No i-frames. Medium cooldown (1200ms). Fizzle at low XP.
 *
 * Unlike the normal dash (which fires through DashComponent.startDash and
 * toggles isDashing/hasIFrames), Wraith consumes a dash charge directly and
 * repositions the player via a teleport-style movement dep. This avoids
 * triggering the movement-break check that would end Hide/Stealth.
 *
 * Upgrade hooks wired through resolver scope 'wraith':
 * - Quickstep (cooldownReduction): fraction reduction on cooldown
 * - Afterimage (afterimageDurationMs, afterimageDrawsInvestigation):
 *   spawns investigation decoy at origin on activation
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Wraith
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface WraithDeps {
  readonly targeting: {
    shouldFizzle(): boolean;
    /** Current player facing direction (normalized). */
    getFacingDirection(): { x: number; y: number };
  };
  readonly dash: {
    /** Current available dash charges. */
    readonly charges: number;
    /** Consume one dash charge without starting a physics dash. */
    consumeCharge(): void;
  };
  readonly movement: {
    /**
     * Reposition the player by an offset in world pixels.
     * This is a teleport-style move — it does not trigger movement-break
     * checks for Hide/Stealth.
     */
    reposition(dx: number, dy: number): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly afterimage?: {
    /**
     * Spawn an investigation decoy at the given world position.
     * Called when Afterimage upgrade is unlocked.
     */
    spawnDecoy(x: number, y: number, durationMs: number): void;
    /** Current player world position (for decoy spawn at origin). */
    getCurrentPosition(): { x: number; y: number };
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface WraithConfig {
  readonly cooldownMs: number;
  readonly rangePx: number;
}

const DEFAULT_CONFIG: WraithConfig = {
  cooldownMs: 1200,              // medium cooldown tier
  rangePx: 32,                   // ~1 tile at 32px/tile
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class WraithSkillEffect implements SkillEffect {
  readonly skillId = 'wraith';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: WraithDeps;
  private readonly config: WraithConfig;

  private _cooldownRemaining = 0;
  private _fizzled = false;
  private _activationCount = 0;
  private _resolvedCooldownMs = 0;

  constructor(deps: WraithDeps, config: Partial<WraithConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get fizzled(): boolean { return this._fizzled; }
  get activationCount(): number { return this._activationCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && this.deps.dash.charges > 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — consumes cooldown but does not reposition
    this._fizzled = this.deps.targeting.shouldFizzle();

    // Resolve cooldown: base minus Quickstep reduction (scope: 'wraith')
    const cooldownReduction = this.deps.resolver.getAdditiveEffect(
      'wraith', 'cooldownReduction',
    );
    this._resolvedCooldownMs = this.config.cooldownMs * (1 - cooldownReduction);

    if (this._fizzled) {
      this._cooldownRemaining = this._resolvedCooldownMs;
      // Consume dash charge even on fizzle — the charge is spent attempting
      this.deps.dash.consumeCharge();
      return false;
    }

    // Resolve Afterimage: spawn investigation decoy at origin before moving
    const hasAfterimage = this.deps.resolver.hasEffect('wraith', 'afterimageDrawsInvestigation');
    if (hasAfterimage && this.deps.afterimage) {
      const afterimageDuration = this.deps.resolver.getAdditiveEffect(
        'wraith', 'afterimageDurationMs',
      );
      const origin = this.deps.afterimage.getCurrentPosition();
      this.deps.afterimage.spawnDecoy(origin.x, origin.y, afterimageDuration);
    }

    // Consume dash charge and reposition
    this.deps.dash.consumeCharge();
    const dir = this.deps.targeting.getFacingDirection();
    this.deps.movement.reposition(dir.x * this.config.rangePx, dir.y * this.config.rangePx);

    // Start cooldown
    this._cooldownRemaining = this._resolvedCooldownMs;
    this._activationCount++;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
